import { useEffect, useMemo, useRef, useState } from "react";

type UseVirtualizedRowsOptions = {
  itemCount: number;
  rowHeight: number;
  overscan?: number;
  enabled?: boolean;
  resetKey?: string | number;
};

export const useVirtualizedRows = ({
  itemCount,
  rowHeight,
  overscan = 8,
  enabled = true,
  resetKey,
}: UseVirtualizedRowsOptions) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const updateViewportHeight = () => {
      setViewportHeight(container.clientHeight);
    };

    updateViewportHeight();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        updateViewportHeight();
      });
      observer.observe(container);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateViewportHeight);
    return () => window.removeEventListener("resize", updateViewportHeight);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = 0;
    }
    setScrollTop(0);
  }, [itemCount, resetKey]);

  const virtualizationEnabled = enabled && itemCount > 0;

  const metrics = useMemo(() => {
    if (!virtualizationEnabled) {
      return {
        startIndex: 0,
        endIndex: itemCount,
        topSpacerHeight: 0,
        bottomSpacerHeight: 0,
      };
    }

    const safeViewportHeight = viewportHeight || rowHeight * 12;
    const visibleCount = Math.max(1, Math.ceil(safeViewportHeight / rowHeight));
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / rowHeight) - overscan,
    );
    const endIndex = Math.min(
      itemCount,
      startIndex + visibleCount + overscan * 2,
    );

    return {
      startIndex,
      endIndex,
      topSpacerHeight: startIndex * rowHeight,
      bottomSpacerHeight: Math.max(0, (itemCount - endIndex) * rowHeight),
    };
  }, [
    itemCount,
    overscan,
    rowHeight,
    scrollTop,
    viewportHeight,
    virtualizationEnabled,
  ]);

  return {
    scrollContainerRef,
    setScrollTop,
    virtualizationEnabled,
    ...metrics,
  };
};
